import * as ts_Query from '@tanstack/react-query'
import { disconnect } from '@wagmi/core'
import { useConnection } from 'wagmi'

import * as Account from '#/lib/account.js'
import { api } from '#/lib/api.js'
import { wagmiConfig } from '#/lib/config.js'
import { readOnly } from '#/lib/connectors/readOnly.js'
import * as Zone from '#/lib/zone.js'

export function useSessionAddress() {
  return getSessionAddress(useConnection())
}

/** Returns the authenticated session address for a wagmi connection state. */
export function getSessionAddress(
  connection: Pick<ReturnType<typeof useConnection>, 'address' | 'connector'>,
) {
  if (connection.connector?.type === readOnly.type) return undefined
  return connection.address ?? undefined
}

/** Session-scoped query helpers rooted at the authenticated wagmi address. */
export namespace session {
  /** Returns a session-scoped query key under the shared session root. */
  export function queryKey(address: string | null | undefined, ...parts: readonly unknown[]) {
    return ['session', address?.toLowerCase() ?? null, ...parts] as const
  }

  /** Clears all client-side state tied to one authenticated session. */
  export async function clearState(
    queryClient: ts_Query.QueryClient,
    address?: string | null | undefined,
  ) {
    if (!address) return
    await queryClient.cancelQueries({ queryKey: queryKey(address) })
    queryClient.removeQueries({ queryKey: queryKey(address) })
    await Account.clear(queryClient, address)
    await Zone.clear(queryClient)
  }
}

/** Query namespace for the current user profile (`/api/auth/me`). */
export namespace me {
  /** Returns the session-scoped query key for the current user profile. */
  export function queryKey(address?: string | null | undefined) {
    return session.queryKey(address, 'auth', 'me')
  }

  /** Hook to fetch the current user profile. */
  export function useQuery() {
    const address = useSessionAddress()
    return ts_Query.useQuery({
      enabled: Boolean(address),
      queryKey: queryKey(address),
      queryFn: async () => {
        const response = await api.api.auth.me.$get()
        if (response.status === 451) throw new Error('COMPLIANCE_BLOCKED')
        if (!response.ok) return null
        return response.json()
      },
      retry: (_, error) => error.message !== 'COMPLIANCE_BLOCKED',
    })
  }

  /** Returns a callback that seeds the query cache with known user data. */
  export function useSetData() {
    const queryClient = ts_Query.useQueryClient()
    return (data: { address: string; email: string | null; username: string | null }) =>
      queryClient.setQueryData(queryKey(data.address), data)
  }
}

/** Logout namespace — clears the session cookie and disconnects the wagmi connector. */
export namespace logout {
  export async function mutate() {
    const logout = api.api.auth.logout.$post()
    const disconnected = [] as PromiseSettledResult<void>[]
    const connectors = [...wagmiConfig.state.connections.values()].map((x) => x.connector)

    // wagmi only disconnects the current connector per call. Drain every live
    // connection so a second connector (for example Tempo Wallet) can't keep
    // the app in a connected state after logout.
    for (const connector of connectors)
      try {
        await disconnect(wagmiConfig, { connector })
        disconnected.push({ status: 'fulfilled', value: undefined })
      } catch (error) {
        disconnected.push({ status: 'rejected', reason: error })
      }

    const result = await Promise.allSettled([logout])
    const logoutResult = result[0]
    const failedDisconnect = disconnected.find((x) => x.status === 'rejected')
    if (failedDisconnect?.status === 'rejected' && wagmiConfig.state.current)
      throw failedDisconnect.reason as Error
    if (logoutResult.status === 'rejected' && failedDisconnect?.status === 'rejected')
      throw (failedDisconnect.reason ?? logoutResult.reason) as Error
  }

  /** Hook wrapping `logout.mutate` with React Query mutation state. */
  export function useMutation(
    options: Omit<ts_Query.UseMutationOptions<void, Error, void>, 'mutationFn'> = {},
  ) {
    const address = useSessionAddress()
    const queryClient = ts_Query.useQueryClient()
    return ts_Query.useMutation({
      mutationFn: mutate,
      ...options,
      onSuccess: async (data, variables, onMutateResult, context) => {
        await session.clearState(queryClient, address)
        await options.onSuccess?.(data, variables, onMutateResult, context)
      },
    })
  }
}
